/**
 * @fileoverview Theme store for managing dark/light/system mode.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type ThemeMode = 'light' | 'dark' | 'system';

interface ThemeState {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
}

/**
 * Apply theme to document
 */
function applyTheme(mode: ThemeMode): void {
  if (typeof document === 'undefined') return;

  const root = document.documentElement;
  const isDark =
    mode === 'dark' ||
    (mode === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  root.classList.toggle('dark', isDark);
}

/**
 * Theme store with localStorage persistence
 */
export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      mode: 'system',
      setMode: (mode) => {
        set({ mode });
        applyTheme(mode);
      },
    }),
    {
      name: 'theme-storage',
      storage: createJSONStorage(() => localStorage),
      onRehydrateStorage: () => (state) => {
        if (state) {
          applyTheme(state.mode);
        }
      },
    }
  )
);

/**
 * Initialize theme on app load (call once in main.tsx)
 */
export function initializeTheme(): void {
  if (typeof window === 'undefined') return;

  const stored = localStorage.getItem('theme-storage');
  let mode: ThemeMode = 'system';

  if (stored) {
    try {
      const { state } = JSON.parse(stored);
      mode = state.mode || 'system';
    } catch {
      // ignore
    }
  }

  applyTheme(mode);

  // Listen for system theme changes
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  mediaQuery.addEventListener('change', () => {
    const currentMode = useThemeStore.getState().mode;
    if (currentMode === 'system') {
      applyTheme('system');
    }
  });
}
